import { z } from 'zod';
import { type AnyType } from './any';
import { type ApiSort } from './api/sort';
import {
    ExternalCampaignTriggerRequestSchema,
    JourneyFiltersConfigSchema,
    type ExternalCampaignTriggerRequest,
} from './campaign';
import { CommunicationChannel } from './communications';
import { PeriodType } from './deliveryControl';
import {
    CommonEventColumns,
    ReservedEventColumns,
    type EventMapperSchema,
    type JourneyEventNames,
} from './events';
import { type DimensionType } from './field';
import { type UnitOfTime } from './filter';
import { type NestedMetricQueryGroup } from './metricQuery';
import { type CompiledRelationTable } from './relation/compiledTables';
import { type LightdashUser, type SessionUser } from './user';

export const JOURNEY_DYNAMIC_VARIABLE_PREFIX = '$.';
export const BUSINESS_EVENT_AUDIENCE_PREFIX = 'business_events_';
export const WAIT_FOR_WH_CACHE_BLOCK_ID = 'wait-for-cache';
export const CallApiConcatOperator = '§';

export type JourneyEntryLogic = {
    cooldown: number; // milliseconds
    killExisting: boolean;
    contextId:
        | CommonEventColumns.CLIENT_EVENT_ID
        | ReservedEventColumns.USER_ID;
    contextConcurrency: number; // -1
    contextTotal: number; // -1
    uiConfig?: Partial<{
        cooldownType: PeriodType;
    }>;
    entryLimit?: number;
    entryNodeId?: string;
};

export type ConversionTrigger = BaseTrigger & {
    deadline: number; // ms
    uiConfig?: Partial<{
        granularity: PeriodType;
    }>;
    associatedAudienceIds?: string[];
    cronConfig?: CronConfig;
} & Pick<
        JourneyCommonTriggerFields,
        'isAudienceInAndOutTrigger' | 'associatedAudienceFilterConfig'
    >;

export type JourneyTriggerConfig = {
    entry: Array<EntryTrigger>;
    exit?: Array<ExitTrigger>;
    conversion?: Array<ConversionTrigger>;
    signals?: Array<BaseTrigger>;
};

export type EntryTrigger = BaseTrigger & JourneyCommonTriggerFields;

export type CronConfig = {
    cron: string | undefined;
    jobId: string | undefined;
    cronStartAt?: Date;
    cronEndAt?: Date;
};

export type JourneyCommonTriggerFields = {
    associatedAudienceFilterConfig?: NestedMetricQueryGroup;
    isBusinessEventTrigger?: boolean;
    isScheduledAudienceTrigger?: boolean;
    cronConfig?: CronConfig;
    audienceDiffEnabled?: boolean;
    isAudienceInAndOutTrigger?: boolean;
    isOtherJourneysTrigger?: boolean;
};

export type ExitTrigger = BaseTrigger &
    Pick<
        JourneyCommonTriggerFields,
        | 'associatedAudienceFilterConfig'
        | 'isBusinessEventTrigger'
        | 'audienceDiffEnabled'
    >;

export type BaseTrigger = {
    id: string;
    eventName: string;
    eventSource: string;
    filterConfig?: JourneyFiltersConfig;
    metadata?: Partial<JourneyNode>;
};
export function isTrigger(
    value: EntryTrigger | JourneyNode,
): value is EntryTrigger {
    return 'eventName' in value && 'eventSource' in value;
}
export function isBusinessEventTrigger(
    value: EntryTrigger,
): value is EntryTrigger {
    return (
        'isBusinessEventTrigger' in value &&
        value.isBusinessEventTrigger === true
    );
}

export function isAudienceInAndOutTrigger(
    value: EntryTrigger,
): value is EntryTrigger {
    return (
        'isAudienceInAndOutTrigger' in value &&
        value.isAudienceInAndOutTrigger === true
    );
}

export function isOtherJourneysTrigger(
    value: EntryTrigger,
): value is EntryTrigger {
    return (
        'isOtherJourneysTrigger' in value &&
        value.isOtherJourneysTrigger === true
    );
}

export function isScheduledAudienceTrigger(
    value: EntryTrigger,
): value is EntryTrigger {
    return (
        'isScheduledAudienceTrigger' in value &&
        value.isScheduledAudienceTrigger === true
    );
}

export enum JourneyStatus {
    DRAFT = 'DRAFT',
    ACTIVATING = 'ACTIVATING',
    ACTIVATION_FAILED = 'ACTIVATION_FAILED',
    ACTIVE = 'ACTIVE',
    SCHEDULED = 'SCHEDULED',
    CANCELED = 'CANCELED',
}

export enum JourneyVersionStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    CANCELED = 'CANCELED',
}

export enum JourneyExecutionStatus {
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    TERMINATED = 'TERMINATED',
}

export enum JourneyQueryParams {
    STATUS = 'status',
    NAME = 'name',
    IS_DELETED = 'isDeleted',
    INCLUDES_TAGS = 'includesTags',
    EXCLUDES_TAGS = 'excludesTags',
    SORT = 'sort',
    ORDER = 'order',
    NEW_VERSION = 'newVersion',
    VERSION_ID = 'versionId',
    REDUCED = 'reduced',
    VIEW_ID = 'viewId',
    FILTERS = 'filters',
}

export type Journey = {
    id: string;
    projectId: string;
    name: string;
    description?: string;
    displayName: string;
    startDate: Date | undefined;
    status: JourneyStatus;
    endDate: Date | undefined;
    jobId: string;
    createdAt: Date;
    createdBy: Pick<LightdashUser, 'userUuid' | 'firstName' | 'lastName'>;
    updatedAt: Date;
    updatedBy: Pick<
        LightdashUser,
        'userUuid' | 'firstName' | 'lastName'
    > | null;
    cancelledAt: Date | undefined;
    cancelledBy: string | undefined;
    isDeleted: boolean;
    tags: string[] | undefined;
    currentVersionId: string | undefined;
};

export type JourneyAndVersionConfig = Journey & JourneyVersionConfig;

export type JourneyVersionConfig = Pick<
    JourneyVersion,
    | 'versionName'
    | 'config'
    | 'triggers'
    | 'entryLogic'
    | 'associatedAudienceIds'
>;

export type JourneyVersion = {
    id: string;
    journeyId: string;
    versionName: string;
    config: JourneyNodesConfig;
    triggers: JourneyTriggerConfig;
    entryLogic: JourneyEntryLogic;
    status: JourneyVersionStatus;
    createdAt: Date;
    createdBy: string;
    associatedAudienceIds: string[] | undefined;
};

export type JourneyCreatePayload = Partial<
    Omit<
        Journey,
        | 'id'
        | 'createdAt'
        | 'createdBy'
        | 'updatedAt'
        | 'updatedBy'
        | 'cancelledAt'
        | 'cancelledBy'
        | 'isDeleted'
        | 'status'
        | 'jobId'
    >
> &
    Partial<JourneyVersionConfig>;

export type JourneyAndExecutionCount = JourneyAndVersionConfig & {
    totalExecutions: number | undefined;
    currentExecutions: number | undefined;
    convertedExecutions: number | undefined;
    versions?: JourneyVersionSummary[];
};

export type JourneyNameDetailsUpdateRequest = Pick<
    Journey,
    'name' | 'description' | 'tags'
>;
export type ReducedJourneyAndExecutionCount = Omit<
    JourneyAndExecutionCount,
    'config'
>;

export type JourneyVersionSummary = {
    id: string;
    journeyId: string;
    versionName: string;
    status: JourneyVersionStatus;
    createdBy: string;
    createdAt: Date;
};

export type JourneyAndExecutionCountList = {
    data: JourneyAndExecutionCount[] | null;
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export type ReducedJourneyList = {
    data: ReducedJourneyAndExecutionCount[] | null;
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export type JourneyList = {
    data: Journey[] | null;
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export type FindJourneysResponseDto = {
    status: string;
    results: JourneyList;
};

export type FindReducedJourneysResponseDto = {
    status: string;
    results: ReducedJourneyList;
};

export type GetJourneyResponseDto = {
    status: string;
    results: JourneyAndVersionConfig;
};

export type GetJourneyTemplates = {
    status: string;
    results: JourneyTemplate[];
};

export type JourneyTemplate = {
    id: string;
    projectUuid: string;
    configType: ConfigType;
    config: JourneyNode[];
    createdBy: Pick<LightdashUser, 'userUuid' | 'firstName' | 'lastName'>;
    updatedBy: Pick<
        LightdashUser,
        'userUuid' | 'firstName' | 'lastName'
    > | null;
    createdAt?: Date;
};

export type FindJourneyPayload = {
    user: SessionUser;
    organizationUuid: string;
    projectId: string;
    paginate: {
        perPage: number;
        currentPage: number;
    };
    status?: string[];
    name?: string;
    isDeleted?: boolean;
    includesTags?: string;
    excludesTags?: string;
    sortArgs?: ApiSort;
    createdAfter?: Date;
    createdBefore?: Date;
    reduced?: boolean;
    viewId?: string;
    filters?: string;
};

// Workflow types

export enum ActionType {
    WAIT = 'WAIT',
    SPLIT = 'SPLIT',
    FILTER = 'FILTER',
    SET = 'SET',
    SEND_MESSAGE = 'SEND_MESSAGE',
    API = 'API',
    WAIT_UNTIL = 'WAIT_UNTIL',
    UPDATE_TRAIT = 'UPDATE_TRAIT',
    EXPERIMENT = 'EXPERIMENT',
    SEND_TO_JOURNEY = 'SEND_TO_JOURNEY',
}

export type ConfigType = ActionType | 'JOURNEY_BRANCH';

export const InlineActivities = [
    ActionType.WAIT,
    ActionType.FILTER,
    ActionType.SET,
    ActionType.WAIT_UNTIL,
    ActionType.EXPERIMENT,
    ActionType.SPLIT,
];

export type BaseAction = {
    type: ActionType;
};

export type WaitAction = BaseAction & {
    type: ActionType.WAIT;
    config: {
        timeConfig: DurationTimeConfig | DateTimeConfig | HoldConfig;
        responseConfig: ResponseConfig;
    };
    uiConfig?: Partial<{
        timeGranularity: PeriodType;
    }>;
    actionConfig?: WaitActionConfig;
};

export type SplitAction = BaseAction & {
    type: ActionType.SPLIT;
    config: {
        responseConfig: ResponseConfig;
    };
    actionConfig?: SplitActionConfig;
};

export type UpdateTraitConfig = {
    name: string;
    updateConfig:
        | UpdateTraitDateConfig
        | UpdateNumberConfig
        | UpdateTraitBaseConfig;
}[];

export type UpdateTraitAction = BaseAction & {
    type: ActionType.UPDATE_TRAIT;
    config: {
        updateTraitConfig: UpdateTraitConfig;
        responseConfig: ResponseConfig;
    };
};

export type FilterAction = BaseAction & {
    type: ActionType.FILTER;
    config: {
        filterConfig: JourneyFiltersConfig;
        responseConfig: ResponseConfig;
    };
    actionConfig?: FilterActionConfig;
};

export type SetAction = BaseAction & {
    type: ActionType.SET;
    config: {
        payloadMapper: Record<string, AnyType>;
        responseConfig: ResponseConfig;
    };
    actionConfig?: SetActionConfig;
};

export type SendMessageAction = BaseAction & {
    type: ActionType.SEND_MESSAGE;
    config: {
        messageConfig: ExternalCampaignTriggerRequest;
        campaignId?: string;
        responseConfig: ResponseConfig;
        webhookConfig?: WebhookConfig;
    };
    actionConfig?: SendMessageActionConfig;
};

export type ApiAction = BaseAction & {
    type: ActionType.API;
    config: {
        apiConfig: ApiConfig;
        responseConfig: ResponseConfig;
    };
    actionConfig?: ApiActionConfig;
    warehouseFields: string[];
};

export type ExperimentAction = BaseAction & {
    type: ActionType.EXPERIMENT;
    config: {
        experimentConfig: ExperimentConfig;
        responseConfig: ResponseConfig;
    };
    actionConfig?: undefined;
};

export type SendToJourneyAction = BaseAction & {
    type: ActionType.SEND_TO_JOURNEY;
    config: {
        sendToJourneyConfig: SendToJourneyConfig;
        responseConfig: ResponseConfig;
    };
    actionConfig?: SendToJourneyActionConfig;
};

export type WaitUntilAction = BaseAction & {
    type: ActionType.WAIT_UNTIL;
    config: {
        timeConfig: DurationTimeConfig | DateTimeConfig;
        eventConfig: EventConfig;
        responseConfig: ResponseConfig;
    };
    uiConfig?: Partial<{
        timeGranularity: PeriodType;
        transientEventFilterTable: string; // Info: This is the table name of the event filter that is added to the filter action but no property is selected
        eventLabel: string;
    }>;
    actionConfig?: WaitUntilActionConfig;
};

export type JourneyAction =
    | WaitAction
    | FilterAction
    | SetAction
    | SendMessageAction
    | ApiAction
    | WaitUntilAction
    | SplitAction
    | UpdateTraitAction
    | ExperimentAction
    | SendToJourneyAction;

export enum ProcessingType {
    SYNC = 'sync',
    ASYNC = 'async',
}

export type ResponseConfig = {
    type?: ProcessingType;
    responseMapper?: { [key: string]: AnyType };
};

export type WebhookConfig = {
    type: ProcessingType;
    eventConfig: EventConfig;
};

export type EventConfig = {
    eventName: string;
    eventSource: string;
    filterConfig?: JourneyFiltersConfig;
    type?: ProcessingType;
};

export type ApiConfig = {
    payloadMapper: string;
    httpConfig: HttpConfig;
};

export type ExperimentConfig = {
    experimentId: string;
    experimentName: string;
    conversionEvent: ConversionTrigger;
    conclusionConfig?: {
        // this is enough to assume auto selection of winning path
        time: number; // if this value is not null then on the day of conclusion winning path will be selected
        granularity: PeriodType;
    };
    variantConfig: ExperimentVariant[];
};

export type SendToJourneyConfig = {
    destinationJourneyIds: string[];
};

export type ExperimentVariant = {
    variantId: string;
    variantName: string;
    distributionPercentage: number;
    nodeId: string;
    isControl: boolean;
    winningVariant: boolean; // default to false
};

export type JourneyNodeEventsData = {
    projectId: string;
    journeyId: string;
    userId: string | null;
    contextId: string | null;
    dslVersion: string | undefined;
    executionId: string;
    nodeId: string;
    eventName: JourneyEventNames;
    parentJourneyId?: string;
    eventMetadata?:
        | JourneyEnteredMetadata
        | JourneyExitedMetadata
        | JourneyConvertedMetadata
        | JourneyWaitUntilMetadata
        | JourneyAPIMetadata
        | JourneySendMessageMetadata;
};

export type ApiRequestConfig = {
    payloadMapper: AnyType;
    httpConfig: HttpRequestConfig;
    retryConfig?: RetryConfig;
};

export type HttpConfig = {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers: string;
};

export type HttpRequestConfig = {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers: AnyType;
};

export type RetryConfig = {
    retryCount: number;
    retryDelay: number;
};

export type Branch = {
    destination: string;
    isDefault: boolean;
    conditions?: JourneyFiltersConfig;
};

export enum BranchConcurrencyTypes {
    PARALLEL = 'PARALLEL',
    SEQUENTIAL = 'SEQUENTIAL',
}

export enum BranchConditionalTypes {
    IFELSE = 'IFELSE',
    IFIF = 'IFIF',
}

export type ParallelBranches = {
    type: BranchConcurrencyTypes.PARALLEL;
    branches: Branch[];
};

export type SequentialBranches = {
    type: BranchConcurrencyTypes.SEQUENTIAL;
    branches: Branch[];
};

export type BranchConfig = {
    type: BranchConditionalTypes;
    children: ParallelBranches | SequentialBranches;
};

export type JourneyNodeExitConfig = {
    notify: boolean;
    notifyConfig: {
        type: 'EVENT';
        config: {
            eventName: string;
            payloadMapper: Record<string, AnyType>;
        };
    };
};

export type JourneyNode = {
    id: string;
    title: string;
    description: string;
    actions: JourneyAction[];
    branchConfig?: BranchConfig;
    exitConfig?: JourneyNodeExitConfig;
    metadata: // Keep experiment_id in the metadata for experiment scope in the journey UI
    | {
              blockId: string;
          }
        | undefined;
};

export type JourneyNodesConfig = {
    nodes: JourneyNode[];
};

export type JourneyTemplatePayload = {
    nodes: JourneyNode[];
    configType: ConfigType;
};

// (schemas moved to the end of file)

export type ActivityResult = {
    responseData?: Record<string, AnyType>;
    journeyParams?: JourneyData;
    nextNodes?: JourneyNode[];
    payload?: AnyType;
};

// For FE
export enum JourneyGroup {
    'TRIGGER' = 'trigger',
    'ACTION' = 'action',
    'FLOW_CONTROL' = 'Flow control',
    'DELAY' = 'delay',
}

export interface JourneyBlockConfig {
    id: string;
    title: string;
    description: string | undefined;
    group: JourneyGroup;
    actions: ActionTypeWithConfig<ActionType>[];
}

interface BaseActionConfig {
    allowEveryoneElse: boolean;
}

// Define the specific config types for each action type
export type WaitActionConfig = BaseActionConfig & {
    type: TimeConfigType;
};

export type WaitUntilActionConfig = BaseActionConfig & {};

export type FilterActionConfig = BaseActionConfig & {};

export type SendMessageActionConfig = BaseActionConfig & {
    channelType: CommunicationChannel;
};

export type ApiActionConfig = BaseActionConfig & {};

export type SetActionConfig = BaseActionConfig & {};

export type SplitActionConfig = BaseActionConfig & {};

export type ExperimentActionConfig = BaseActionConfig & {};

export type SendToJourneyActionConfig = BaseActionConfig & {};

export type ActionTypeConfigMap = {
    WAIT: WaitActionConfig;
    SPLIT: SplitActionConfig;
    WAIT_UNTIL: WaitUntilActionConfig;
    FILTER: FilterActionConfig;
    SEND_MESSAGE: SendMessageActionConfig;
    API: ApiActionConfig;
    SET: SetActionConfig;
    UPDATE_TRAIT: UpdateTraitConfig;
    EXPERIMENT: ExperimentActionConfig;
    SEND_TO_JOURNEY: SendToJourneyActionConfig;
};

export type ActionTypeWithConfig<T> = {
    actionType: T;
    config?:
        | WaitActionConfig
        | WaitUntilActionConfig
        | FilterActionConfig
        | SendMessageActionConfig
        | ApiActionConfig
        | SetActionConfig
        | SplitActionConfig
        | ExperimentActionConfig
        | SendToJourneyActionConfig;
};

export type JourneyBlocksMetadata = Omit<JourneyBlockConfig, 'actions'> & {
    actions: ActionTypeWithConfig<ActionType>[];
    allowedInExperiment: boolean;
};

export type JourneyBlocksList = JourneyBlocksMetadata[];

// Filter Schema

export enum JourneyTableType {
    PRIMARY = 'PRIMARY',
    EVENT = 'EVENT',
    RELATED = 'RELATED',
    AUDIENCE = 'AUDIENCE',
    API = 'API',
}

export type JourneyDimension = {
    type: DimensionType;
    label: string;
    name: string;
    table: string;
};

export type JourneyTable = {
    name: string;
    type: JourneyTableType;
    label: string;
    eventName?: string;
    eventSource?: string;
    nodeId?: string;
    dimensions: Record<string, JourneyDimension>;
};

export type JourneyEventParamsRequest = Pick<
    JourneyVersion,
    'config' | 'entryLogic' | 'triggers'
>;

export type JourneyDataSchema = {
    baseTable: string;
    journeyId: string;
    userId: string | undefined;
    contextId: string;
    tables: Record<string, JourneyTable>;
    relationTables: { [tableName: string]: CompiledRelationTable };
};

export type JourneyData = {
    projectId: string;
    journeyId: string;
    userId: string | null;
    contextId: string | null;
    dslVersion: string | undefined;
    srtCustomer360?: Record<string, AnyType>; // will be populated in workflows/activities whenever needed.
    currentNodeId?: string;
    executionId?: string;
} & Record<string, AnyType>;

export enum JourneyConditionalOperator {
    NULL = 'isNull',
    NOT_NULL = 'notNull',
    EQUALS = 'equals',
    NOT_EQUALS = 'notEquals',
    STARTS_WITH = 'startsWith',
    ENDS_WITH = 'endsWith',
    INCLUDE = 'include',
    NOT_INCLUDE = 'doesNotInclude',
    LESS_THAN = 'lessThan',
    LESS_THAN_OR_EQUAL = 'lessThanOrEqual',
    GREATER_THAN = 'greaterThan',
    GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual',
    IN_THE_PAST = 'inThePast',
    NOT_IN_THE_PAST = 'notInThePast',
    IN_THE_NEXT = 'inTheNext',
    NOT_IN_THE_NEXT = 'notInTheNext',
    IN_THE_CURRENT = 'inTheCurrent',
    NOT_IN_THE_CURRENT = 'notInTheCurrent',
    IN_BETWEEN = 'inBetween',
    EQUALS_CASE_INSENSITIVE = 'equalsCaseInsensitive',
    NOT_EQUALS_CASE_INSENSITIVE = 'notEqualsCaseInsensitive',
}

export type JourneyContextExecutionsResponse = {
    journeysToTerminate: string[];
    startNew: boolean;
};

export type JourneyConditionalRule<
    O = JourneyConditionalOperator,
    V = AnyType,
> = {
    operator: O;
    values?: V[];
};

export type JourneyFieldTarget = {
    fieldId: string;
    fieldRef?: string;
};
export type JourneyDateFilterSettings = {
    unitOfTime?: UnitOfTime;
    completed?: boolean;
};

export interface JourneyFilterRule<
    O = JourneyConditionalOperator,
    T = JourneyFieldTarget,
    V = AnyType,
    S = AnyType,
> extends JourneyConditionalRule<O, V> {
    target: T;
    settings?: S;
}

export type JourneyFilterGroupItem = JourneyFilterGroup | JourneyFilterRule;

export type OrJourneyFilterGroup = {
    or: Array<JourneyFilterGroupItem>;
};

export type AndJourneyFilterGroup = {
    and: Array<JourneyFilterGroupItem>;
};

export type JourneyFilterGroup = OrJourneyFilterGroup | AndJourneyFilterGroup;

export type JourneyFiltersConfig = {
    id: string;
    journeyFilters: JourneyFilterGroup | undefined;
    audienceFilters:
        | {
              filterJoinType: 'and' | 'or';
              filterConfig: NestedMetricQueryGroup;
              compiledAudienceId: string | undefined;
          }
        | undefined;
};

export const isOrJourneyFilterGroup = (
    value: JourneyFilterGroupItem,
): value is OrJourneyFilterGroup => 'or' in value;

export const isAndJourneyFilterGroup = (
    value: JourneyFilterGroupItem,
): value is AndJourneyFilterGroup => 'and' in value;

export const isJourneyFilterGroup = (
    value: JourneyFilterGroupItem,
): value is JourneyFilterGroup =>
    isOrJourneyFilterGroup(value) || isAndJourneyFilterGroup(value);

export const isJourneyFilterRule = (
    value: JourneyConditionalRule | JourneyFilterGroupItem,
): value is JourneyFilterRule =>
    'id' in value && 'target' in value && 'operator' in value;

export type JourneyEventMapperSchema = EventMapperSchema & {
    sourceLabel: CommunicationChannel | undefined;
};

export type JourneyPublishConfig = Partial<
    Pick<Journey, 'startDate' | 'endDate' | 'status'> &
        Pick<Journey, 'currentVersionId'>
>;

export type JourneyExecutionCount = {
    [journeyId: string]: {
        running: number;
    };
};

// Temporal types --start

export enum TemporalStatus {
    RUNNING = 'Running',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
    CONTINUED_AS_NEW = 'CONTINUED_AS_NEW',
    TIMED_OUT = 'TIMED_OUT',
    TERMINATED = 'TERMINATED',
}

export enum TemporalDefaultSearchAttributes {
    WORKFLOW_ID = 'WorkflowId',
    WORKFLOW_TYPE = 'WorkflowType',
    START_TIME = 'StartTime',
    EXECUTION_DURATION = 'ExecutionDuration',
    CLOSE_TIME = 'CloseTime',
    STATUS = 'Status',
    EXECUTION_STATUS = 'ExecutionStatus',
}

export enum TemporalCustomSearchAttributes {
    CONTEXT_ID = 'SRT_CUSTOM_JOURNEY_CONTEXT_ID',
    JOURNEY_ID = 'SRT_CUSTOM_JOURNEY_ID',
    JOURNEY_CONVERTED = 'SRT_CUSTOM_JOURNEY_CONVERTED',
    JOURNEY_DSL_VERSION = 'SRT_CUSTOM_JOURNEY_DSL_VERSION',
    JOURNEY_WORKFLOW_VERSION = 'SRT_CUSTOM_JOURNEY_WORKFLOW_VERSION',
    // DSL_VERSION = "SRT_CUSTOM_DSL_VERSION", Add it soon
}

export enum TemporalWorkflowVersions {
    V1 = 'v1',
}

export enum TemporalFilterTypes {
    EQUALS = '=',
    NOT_EQUALS = '!=',
    GREATER_THAN = '>',
    LESS_THAN = '<',
    GREATER_THAN_OR_EQUALS = '>=',
    LESS_THAN_OR_EQUALS = '<=',
    AND = 'and',
    OR = 'or',
    STARTS_WITH = 'STARTS_WITH',
}

export enum TemporalExecutionStatusCode {
    RUNNING = 1,
    COMPLETED = 2,
    FAILED = 3,
    CANCELED = 4,
    CONTINUED_AS_NEW = 6,
    TIMED_OUT = 7,
    TERMINATED = 5,
}
// temporal types --end

export type UpsertJourneyConversion = {
    [TemporalCustomSearchAttributes.JOURNEY_CONVERTED]: boolean;
};
export enum JourneyTriggerType {
    ENTRY = 'entry',
    EXIT = 'exit',
    CONVERSION = 'conversion',
    SIGNAL = 'signal',
}

export type JourneyTagsUpdateRequest = {
    tags: string[];
};

export enum JourneyParamsNames {
    CACHED_PROPERTY = 'SRT_CACHED_PROPERTY',
    CACHED_AUDIENCE = 'SRT_CACHED_AUDIENCE',
    JOURNEY_EVENT = 'SRT_JOURNEY_EVENT',
    SRT_USER_TRAITS = 'SRT_USER_TRAITS',
}

export enum TimeConfigType {
    DURATION = 'DURATION',
    DATE_TIME = 'DATE_TIME',
    HOLD = 'HOLD',
}

export enum UpdateTraitType {
    NUMBER = 'NUMBER',
    DATE_TIME = 'DATE_TIME',
    BASE = 'BASE',
}

export interface DurationTimeConfig {
    timeConfigType: TimeConfigType.DURATION;
    duration: number;
}

export interface DateTimeConfig {
    timeConfigType: TimeConfigType.DATE_TIME;
    dateTime: {
        value: number | string;
        time?: string;
        gracePeriod?: number;
    };
    timeDifference: {
        value: number;
        operator: DifferenceOperator;
    };
}

export enum DayOfWeek {
    SUNDAY = 0,
    MONDAY = 1,
    TUESDAY = 2,
    WEDNESDAY = 3,
    THURSDAY = 4,
    FRIDAY = 5,
    SATURDAY = 6,
}

export enum HoldBoundaryType {
    DAY_OF_WEEK = 'DAY_OF_WEEK',
    DAY_OF_MONTH = 'DAY_OF_MONTH',
}

export type TimeWindow = {
    startHour: number; // 0-23
    startMinute: number; // 0-59
    endHour: number; // 0-23
    endMinute: number; // 0-59
};

export type DayOfWeekConstraint = {
    type: HoldBoundaryType.DAY_OF_WEEK;
    allowedDays: DayOfWeek[]; // Empty array means any day
    timeWindows: TimeWindow[]; // Empty array means any time
};

export type DayOfMonthConstraint = {
    type: HoldBoundaryType.DAY_OF_MONTH;
    allowedDays: number[]; // 1-31, empty array means any day
    timeWindows: TimeWindow[]; // Empty array means any time
};

export type HoldBoundaryConstraint = DayOfWeekConstraint | DayOfMonthConstraint;

export interface HoldConfig {
    timeConfigType: TimeConfigType.HOLD;
    boundaries: HoldBoundaryConstraint;
}

export interface UpdateTraitBaseConfig {
    updateTraitType: UpdateTraitType.BASE;
    config: {
        value: AnyType;
    };
}
export interface UpdateTraitDateConfig {
    updateTraitType: UpdateTraitType.DATE_TIME;
    config: DateTimeConfig;
}

export interface UpdateNumberConfig {
    updateTraitType: UpdateTraitType.NUMBER;
    config: {
        value: number | string;
        diffrence: {
            value: number | string;
            operator: DifferenceOperator;
        };
    };
}

export enum DifferenceOperator {
    ADD = '+',
    SUBTRACT = '-',
}

export const DefaultBranchSelected = 'DEFAULT_BRANCH_SELECTED';

export type JourneyEnteredMetadata = {
    type: JourneyEventNames.JOURNEY_ENTERED;
    data: AnyType; // Not making it MappedEvent because in case of mandatory mapped event fields modification, it will break the backward compatibility and existing queued events
};

export type JourneyWaitUntilMetadata = {
    type: ActionType.WAIT_UNTIL;
    data: AnyType;
};

export type JourneyExitedMetadata = {
    type: JourneyEventNames.JOURNEY_EXITED;
    data?: AnyType;
};

export type JourneyConvertedMetadata = {
    type: JourneyEventNames.JOURNEY_CONVERTED;
    data: AnyType;
};

export type JourneyAPIMetadata = {
    type: ActionType.API;
    data: {
        response: {
            statusCode: number;
            body: AnyType;
        };
        payload?: AnyType;
    };
};

export type JourneySendMessageMetadata = {
    type: ActionType.SEND_MESSAGE;
    data: {
        payload: AnyType;
    };
};

export type JourneyEventPayload = {
    contextId: string | null;
    journeyId: string;
    executionId: string;
    dslVersion: string | null;
    eventName: JourneyEventNames;
    userId: string | null;
    nodeId: string | null;
    timestamp: number;
    parentJourneyId: string | null;
    metadata?:
        | JourneyEnteredMetadata
        | JourneyExitedMetadata
        | JourneyConvertedMetadata
        | JourneyWaitUntilMetadata
        | JourneyAPIMetadata
        | JourneySendMessageMetadata;
};

// Reusable Zod Schemas for Journey Node and related types
export const JourneyNodeMetadataSchema = z.union([
    z.object({
        blockId: z.string({
            invalid_type_error: 'Block ID must be a string',
        }),
    }),
    z.undefined(),
]);

export const BranchSchema = z.object({
    destination: z
        .string({
            invalid_type_error: 'Destination must be a string',
        })
        .min(1, 'Destination is required'),
    isDefault: z.boolean({
        invalid_type_error: 'isDefault must be a boolean',
    }),
    conditions: JourneyFiltersConfigSchema.optional(),
});

export const BranchChildrenSchema = z.object({
    type: z.nativeEnum(BranchConcurrencyTypes, {
        invalid_type_error:
            'Branch concurrency type must be PARALLEL or SEQUENTIAL',
    }),
    branches: z.array(BranchSchema, {
        invalid_type_error: 'Branches must be an array',
    }),
});

export const BranchConfigSchema = z.object({
    type: z.nativeEnum(BranchConditionalTypes, {
        invalid_type_error: 'Branch conditional type must be IFELSE or IFIF',
    }),
    children: BranchChildrenSchema,
});

export const JourneyNodeExitConfigSchema = z
    .object({
        notify: z.boolean({
            invalid_type_error: 'Notify must be a boolean',
        }),
        notifyConfig: z.object({
            type: z.literal('EVENT', {
                invalid_type_error: 'Notify config type must be EVENT',
            }),
            config: z.object({
                eventName: z.string({
                    invalid_type_error: 'Event name must be a string',
                }),
                payloadMapper: z.record(z.unknown(), {
                    invalid_type_error: 'Payload mapper must be a record',
                }),
            }),
        }),
    })
    .optional();

const ProcessingTypeSchema = z.nativeEnum(ProcessingType, {
    invalid_type_error: 'Processing type must be sync or async',
});

const ResponseConfigSchema = z.object(
    {
        type: ProcessingTypeSchema.optional(),
        responseMapper: z
            .record(z.unknown(), {
                invalid_type_error: 'Response mapper must be a record',
            })
            .optional(),
    },
    {
        invalid_type_error: 'Invalid config type for response config',
    },
);

const DurationTimeConfigSchema = z.object({
    timeConfigType: z.literal(TimeConfigType.DURATION, {
        invalid_type_error: 'Time config type must be DURATION',
    }),
    duration: z.number({
        invalid_type_error: 'Duration must be a number',
    }),
});

const TimeWindowSchema = z.object({
    startHour: z.number().min(0).max(23),
    startMinute: z.number().min(0).max(59),
    endHour: z.number().min(0).max(23),
    endMinute: z.number().min(0).max(59),
});

const DayOfWeekConstraintSchema = z.object({
    type: z.literal(HoldBoundaryType.DAY_OF_WEEK),
    allowedDays: z.array(z.nativeEnum(DayOfWeek)),
    timeWindows: z.array(TimeWindowSchema),
});

const DayOfMonthConstraintSchema = z.object({
    type: z.literal(HoldBoundaryType.DAY_OF_MONTH),
    allowedDays: z.array(z.number().min(1).max(31)),
    timeWindows: z.array(TimeWindowSchema),
});

const HoldBoundaryConstraintSchema = z.union([
    DayOfWeekConstraintSchema,
    DayOfMonthConstraintSchema,
]);

const HoldConfigSchema = z.object({
    timeConfigType: z.literal(TimeConfigType.HOLD),
    boundaries: HoldBoundaryConstraintSchema,
});

const DateTimeConfigSchema = z.object({
    timeConfigType: z.literal(TimeConfigType.DATE_TIME, {
        invalid_type_error: 'Time config type must be DATE_TIME',
    }),
    dateTime: z.object({
        value: z.union([z.number(), z.string()], {
            invalid_type_error: 'DateTime value must be a number or string',
        }),
        time: z
            .string({
                invalid_type_error: 'Time must be a string',
            })
            .optional(),
        gracePeriod: z.number().optional(),
    }),
    timeDifference: z.object({
        value: z.number({
            invalid_type_error: 'Time difference value must be a number',
        }),
        operator: z.nativeEnum(DifferenceOperator, {
            invalid_type_error: 'Difference operator must be + or -',
        }),
    }),
});

const TimeConfigSchema = z.union(
    [DurationTimeConfigSchema, DateTimeConfigSchema, HoldConfigSchema],
    {
        invalid_type_error:
            'Time config must be either duration, date time or hold config',
    },
);

const EventConfigSchema = z.object({
    eventName: z.string({
        invalid_type_error: 'Event name must be a string',
    }),
    eventSource: z.string({
        invalid_type_error: 'Event source must be a string',
    }),
    filterConfig: JourneyFiltersConfigSchema.optional(),
    type: ProcessingTypeSchema.optional(),
});

const WebhookConfigSchema = z.object({
    type: ProcessingTypeSchema,
    eventConfig: EventConfigSchema,
});

const BaseActionSchema = z.object({
    type: z.nativeEnum(ActionType, {
        invalid_type_error: 'Action type must be a valid action type',
    }),
});

const BaseActionConfigSchema = z.object({
    allowEveryoneElse: z.boolean({
        invalid_type_error: 'Allow everyone else must be a boolean',
    }),
});

const WaitActionConfigSchema = BaseActionConfigSchema.extend({
    type: z.nativeEnum(TimeConfigType, {
        invalid_type_error:
            'Wait action config type must be DURATION, DATE_TIME or HOLD',
    }),
});

export const WaitActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.WAIT, {
        invalid_type_error: 'Action type must be WAIT',
    }),
    config: z.object({
        timeConfig: TimeConfigSchema,
        responseConfig: ResponseConfigSchema,
    }),
    uiConfig: z
        .object({
            timeGranularity: z.nativeEnum(PeriodType, {
                invalid_type_error:
                    'Time granularity must be a valid period type',
            }),
        })
        .partial()
        .optional(),
    actionConfig: WaitActionConfigSchema.optional(),
});

const SplitActionConfigSchema = BaseActionConfigSchema.extend({});

export const SplitActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.SPLIT, {
        invalid_type_error: 'Action type must be SPLIT',
    }),
    config: z.object({
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: SplitActionConfigSchema.optional(),
});

const FilterActionConfigSchema = BaseActionConfigSchema.extend({});

export const FilterActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.FILTER, {
        invalid_type_error: 'Action type must be FILTER',
    }),
    config: z.object({
        filterConfig: JourneyFiltersConfigSchema,
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: FilterActionConfigSchema.optional(),
});

const SetActionConfigSchema = BaseActionConfigSchema.extend({});

export const SetActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.SET, {
        invalid_type_error: 'Action type must be SET',
    }),
    config: z.object({
        payloadMapper: z.record(z.unknown(), {
            invalid_type_error: 'Payload mapper must be a record',
        }),
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: SetActionConfigSchema.optional(),
});

const SendMessageActionConfigSchema = BaseActionConfigSchema.extend({
    channelType: z.nativeEnum(CommunicationChannel, {
        invalid_type_error:
            'Channel type must be a valid communication channel',
    }),
});

export const SendMessageActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.SEND_MESSAGE, {
        invalid_type_error: 'Action type must be SEND_MESSAGE',
    }),
    config: z.object({
        messageConfig: ExternalCampaignTriggerRequestSchema,
        campaignId: z
            .string({
                invalid_type_error: 'Campaign ID must be a string',
            })
            .optional(),
        responseConfig: ResponseConfigSchema,
        webhookConfig: WebhookConfigSchema.optional(),
    }),
    actionConfig: SendMessageActionConfigSchema.optional(),
});

const HttpConfigSchema = z.object({
    url: z.string({
        invalid_type_error: 'URL must be a string',
    }),
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE'], {
        invalid_type_error: 'HTTP method must be GET, POST, PUT, or DELETE',
    }),
    headers: z.string({
        invalid_type_error: 'Headers must be a string',
    }),
});

const ApiConfigSchema = z.object({
    payloadMapper: z.string({
        invalid_type_error: 'API payload mapper must be a string',
    }),
    httpConfig: HttpConfigSchema,
});

const ApiActionConfigSchema = BaseActionConfigSchema.extend({});

export const ApiActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.API, {
        invalid_type_error: 'Action type must be API',
    }),
    config: z.object({
        apiConfig: ApiConfigSchema,
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: ApiActionConfigSchema.optional(),
    warehouseFields: z.array(z.string(), {
        invalid_type_error: 'Warehouse fields must be an array of strings',
    }),
});

const WaitUntilActionConfigSchema = BaseActionConfigSchema.extend({});

export const WaitUntilActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.WAIT_UNTIL, {
        invalid_type_error: 'Action type must be WAIT_UNTIL',
    }),
    config: z.object({
        timeConfig: TimeConfigSchema,
        eventConfig: EventConfigSchema,
        responseConfig: ResponseConfigSchema,
    }),
    uiConfig: z
        .object({
            timeGranularity: z.nativeEnum(PeriodType, {
                invalid_type_error:
                    'Time granularity must be a valid period type',
            }),
            transientEventFilterTable: z.string({
                invalid_type_error:
                    'Transient event filter table must be a string',
            }),
            eventLabel: z.string({
                invalid_type_error: 'Event label must be a string',
            }),
        })
        .partial()
        .optional(),
    actionConfig: WaitUntilActionConfigSchema.optional(),
});

const UpdateTraitDateConfigSchema = z.object({
    updateTraitType: z.literal(UpdateTraitType.DATE_TIME, {
        invalid_type_error: 'Update trait type must be DATE_TIME',
    }),
    config: DateTimeConfigSchema,
});

const UpdateTraitNumberConfigSchema = z.object({
    updateTraitType: z.literal(UpdateTraitType.NUMBER, {
        invalid_type_error: 'Update trait type must be NUMBER',
    }),
    config: z.object({
        value: z.union([z.number(), z.string()], {
            invalid_type_error:
                'Update trait number value must be a number or string',
        }),
        difference: z.object({
            value: z.union([z.number(), z.string()], {
                invalid_type_error:
                    'Difference value must be a number or string',
            }),
            operator: z.nativeEnum(DifferenceOperator, {
                invalid_type_error: 'Difference operator must be + or -',
            }),
        }),
    }),
});

const UpdateTraitBaseConfigSchema = z.object(
    {
        updateTraitType: z.literal(UpdateTraitType.BASE, {
            invalid_type_error: 'Update trait type must be BASE',
        }),
        config: z.object({
            value: z.any({
                invalid_type_error: 'Update trait base value must be provided',
            }),
        }),
    },
    {
        invalid_type_error: 'Invalid config type for update trait base config',
    },
);

const UpdateTraitConfigSchema = z.array(
    z.object(
        {
            name: z.string({
                invalid_type_error: 'Update trait name must be a string',
            }),
            updateConfig: z.union(
                [
                    UpdateTraitDateConfigSchema,
                    UpdateTraitNumberConfigSchema,
                    UpdateTraitBaseConfigSchema,
                ],
                {
                    invalid_type_error:
                        'Update config must be a valid trait config type',
                },
            ),
        },
        {
            invalid_type_error: 'Invalid config type for update trait config',
        },
    ),
);

export const UpdateTraitActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.UPDATE_TRAIT, {
        invalid_type_error: 'Action type must be UPDATE_TRAIT',
    }),
    config: z.object(
        {
            updateTraitConfig: UpdateTraitConfigSchema,
            responseConfig: ResponseConfigSchema,
        },
        {
            invalid_type_error: 'Invalid config type for update trait action',
        },
    ),
});

// Experiment conclusion config schema
const ExperimentConclusionConfigSchema = z
    .object({
        time: z.number({
            invalid_type_error: 'Experiment conclusion time must be a number',
        }),
        granularity: z.nativeEnum(PeriodType, {
            invalid_type_error:
                'Experiment conclusion granularity must be a valid period type',
        }),
    })
    .optional();

// Experiment variant config schema
const ExperimentVariantConfigSchema = z.object({
    variantId: z.string({
        invalid_type_error: 'Experiment variant ID must be a string',
    }),
    variantName: z.string({
        invalid_type_error: 'Experiment variant name must be a string',
    }),
    distributionPercentage: z.number({
        invalid_type_error: 'Distribution percentage must be a number',
    }),
    nodeId: z.string({
        invalid_type_error: 'Experiment variant node ID must be a string',
    }),
    isControl: z.boolean({
        invalid_type_error: 'Is control must be a boolean',
    }),
    winningVariant: z.boolean({
        invalid_type_error: 'Winning variant must be a boolean',
    }),
});

const BaseTriggerSchema = z.object({
    id: z.string({
        invalid_type_error: 'Trigger ID must be a string',
    }),
    eventName: z.string({
        invalid_type_error: 'Trigger event name must be a string',
    }),
    eventSource: z.string({
        invalid_type_error: 'Trigger event source must be a string',
    }),
    filterConfig: JourneyFiltersConfigSchema.optional(),
    metadata: z
        .unknown({
            invalid_type_error: 'Trigger metadata must be provided',
        })
        .optional(),
});

// Main experiment config schema
const ConversionTriggerSchema = BaseTriggerSchema.extend({
    deadline: z.number({
        invalid_type_error: 'Conversion trigger deadline must be a number',
    }),
    uiConfig: z
        .object({
            granularity: z.nativeEnum(PeriodType, {
                invalid_type_error:
                    'Conversion trigger granularity must be a valid period type',
            }),
        })
        .optional(),
    isAudienceInAndOutTrigger: z
        .boolean({
            invalid_type_error:
                'Is audience in and out trigger must be a boolean',
        })
        .optional(),
    associatedAudienceFilterConfig: z.unknown({
        invalid_type_error:
            'Associated audience filter config must be provided',
    }),
});

const ExperimentConfigSchema = z.object({
    experimentId: z.string({
        invalid_type_error: 'Experiment ID must be a string',
    }),
    experimentName: z.string({
        invalid_type_error: 'Experiment name must be a string',
    }),
    conversionEvent: ConversionTriggerSchema,
    conclusionConfig: ExperimentConclusionConfigSchema,
    variantConfig: z.array(ExperimentVariantConfigSchema, {
        invalid_type_error: 'Experiment variant config must be an array',
    }),
});

export const ExperimentActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.EXPERIMENT, {
        invalid_type_error: 'Action type must be EXPERIMENT',
    }),
    config: z.object({
        experimentConfig: ExperimentConfigSchema,
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: z
        .unknown({
            invalid_type_error: 'Experiment action config must be provided',
        })
        .optional(),
});

const SendToJourneyActionConfigSchema = BaseActionConfigSchema.extend({});

export const SendToJourneyActionSchema = BaseActionSchema.extend({
    type: z.literal(ActionType.SEND_TO_JOURNEY, {
        invalid_type_error: 'Action type must be SEND_TO_JOURNEY',
    }),
    config: z.object({
        sendToJourneyConfig: z.object({
            destinationJourneyIds: z.array(z.string(), {
                invalid_type_error:
                    'Destination journey IDs must be an array of strings',
            }),
        }),
        responseConfig: ResponseConfigSchema,
    }),
    actionConfig: SendToJourneyActionConfigSchema.optional(),
});

export const JourneyActionSchema = z.union(
    [
        WaitActionSchema,
        FilterActionSchema,
        SetActionSchema,
        SendMessageActionSchema,
        ApiActionSchema,
        WaitUntilActionSchema,
        SplitActionSchema,
        UpdateTraitActionSchema,
        ExperimentActionSchema,
        SendToJourneyActionSchema,
    ],
    {
        invalid_type_error: 'Journey action must be a valid action type',
    },
);

export const JourneyNodeSchema = z.object({
    id: z.string({
        invalid_type_error: 'Journey node ID must be a string',
    }),
    title: z.string({
        invalid_type_error: 'Journey node title must be a string',
    }),
    description: z.string({
        invalid_type_error: 'Journey node description must be a string',
    }),
    actions: z.array(JourneyActionSchema, {
        invalid_type_error: 'Journey node actions must be an array',
    }),
    branchConfig: BranchConfigSchema,
    exitConfig: JourneyNodeExitConfigSchema,
    metadata: JourneyNodeMetadataSchema.optional(),
});

const CronConfigSchema = z.object({
    cron: z
        .string({
            invalid_type_error: 'Cron expression must be a string',
        })
        .optional(),
    jobId: z
        .string({
            invalid_type_error: 'Cron job ID must be a string',
        })
        .optional(),
    cronStartAt: z
        .date({
            invalid_type_error: 'Cron start date must be a valid date',
        })
        .optional(),
    cronEndAt: z
        .date({
            invalid_type_error: 'Cron end date must be a valid date',
        })
        .optional(),
});

const JourneyCommonTriggerFieldsSchema = z.object({
    associatedAudienceFilterConfig: z.unknown({
        invalid_type_error:
            'Associated audience filter config must be provided',
    }),
    isBusinessEventTrigger: z
        .boolean({
            invalid_type_error: 'Is business event trigger must be a boolean',
        })
        .optional(),
    isScheduledAudienceTrigger: z
        .boolean({
            invalid_type_error:
                'Is scheduled audience trigger must be a boolean',
        })
        .optional(),
    cronConfig: CronConfigSchema.optional(),
    audienceDiffEnabled: z
        .boolean({
            invalid_type_error: 'Audience diff enabled must be a boolean',
        })
        .optional(),
    isAudienceInAndOutTrigger: z
        .boolean({
            invalid_type_error:
                'Is audience in and out trigger must be a boolean',
        })
        .optional(),
    isOtherJourneysTrigger: z
        .boolean({
            invalid_type_error: 'Is other journeys trigger must be a boolean',
        })
        .optional(),
});

const EntryTriggerSchema = BaseTriggerSchema.and(
    JourneyCommonTriggerFieldsSchema,
);

const ExitTriggerSchema = BaseTriggerSchema.and(
    JourneyCommonTriggerFieldsSchema,
);

const JounreyTriggerConfigSchema = z.object({
    entry: z.array(EntryTriggerSchema, {
        invalid_type_error: 'Entry triggers must be an array',
    }),
    exit: z
        .array(ExitTriggerSchema, {
            invalid_type_error: 'Exit triggers must be an array',
        })
        .optional(),
    conversion: z
        .array(ConversionTriggerSchema, {
            invalid_type_error: 'Conversion triggers must be an array',
        })
        .optional(),
    signals: z
        .array(BaseTriggerSchema, {
            invalid_type_error: 'Signal triggers must be an array',
        })
        .optional(),
});

const JourneyEntryLogicSchema = z.object({
    cooldown: z.number({
        invalid_type_error: 'Journey entry logic cooldown must be a number',
    }),
    killExisting: z.boolean({
        invalid_type_error:
            'Journey entry logic kill existing must be a boolean',
    }),
    contextId: z.union(
        [
            z.literal(CommonEventColumns.CLIENT_EVENT_ID, {
                invalid_type_error: 'Context ID must be CLIENT_EVENT_ID',
            }),
            z.literal(ReservedEventColumns.USER_ID, {
                invalid_type_error: 'Context ID must be USER_ID',
            }),
        ],
        {
            invalid_type_error:
                'Context ID must be either CLIENT_EVENT_ID or USER_ID',
        },
    ),
    contextConcurrency: z.number({
        invalid_type_error: 'Context concurrency must be a number',
    }),
    contextTotal: z.number({
        invalid_type_error: 'Context total must be a number',
    }),
    uiConfig: z
        .object({
            cooldownType: z.nativeEnum(PeriodType, {
                invalid_type_error: 'Cooldown type must be a valid period type',
            }),
        })
        .partial()
        .optional(),
    entryNodeId: z
        .string({
            invalid_type_error: 'Entry node ID must be a string',
        })
        .optional(),
    entryLimit: z
        .number({
            invalid_type_error: 'Entry limit must be a number',
        })
        .optional(),
});

export const JourneyPayloadSchema = z.object({
    id: z
        .string({
            invalid_type_error: 'Journey ID must be a string',
        })
        .min(1, 'Id is required'),
    projectId: z
        .string({
            invalid_type_error: 'Project ID must be a string',
        })
        .min(1, 'Project id is required'),
    name: z
        .string({
            invalid_type_error: 'Journey name must be a string',
        })
        .min(1, 'Name is required'),
    description: z
        .string({
            invalid_type_error: 'Journey description must be a string',
        })
        .optional()
        .nullable(),
    displayName: z.string({
        invalid_type_error: 'Journey display name must be a string',
    }),
    startDate: z
        .date({
            invalid_type_error: 'Journey start date must be a valid date',
        })
        .optional(),
    status: z.enum(Object.values(JourneyStatus) as [string, ...string[]], {
        invalid_type_error: 'Journey status must be a valid status value',
    }),
    endDate: z
        .date({
            invalid_type_error: 'Journey end date must be a valid date',
        })
        .optional()
        .nullable(),
    jobId: z
        .string({
            invalid_type_error: 'Job ID must be a string',
        })
        .nullable(),
    tags: z
        .array(z.string(), {
            invalid_type_error: 'Journey tags must be an array of strings',
        })
        .optional(),
    currentVersionId: z
        .string({
            invalid_type_error: 'Current version ID must be a string',
        })
        .min(1, 'Current version id is required'),
    isDeleted: z.boolean({
        invalid_type_error: 'Is deleted must be a boolean',
    }),
    versionName: z.string({
        invalid_type_error: 'Version name must be a string',
    }),
    config: z
        .object({
            nodes: z.array(JourneyNodeSchema, {
                invalid_type_error: 'Journey config nodes must be an array',
            }),
        })
        .optional(),
    triggers: JounreyTriggerConfigSchema,
    entryLogic: JourneyEntryLogicSchema,
    associatedAudienceIds: z
        .array(z.string(), {
            invalid_type_error:
                'Associated audience IDs must be an array of strings',
        })
        .optional(),
});
